import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@/lib/logger';
import type {
  ChainFailedEvent,
  StepProgressEvent,
} from '@/lib/reasoning/realtime/types';
import { useChatProgress } from '@/lib/reasoning/realtime/useChatProgress';

import type { ReasoningStepSummary } from './useProposalPolling';

interface UseProposalChatProgressOptions {
  onComplete: () => void;
  onError: (error: Error) => void;
}

interface ProgressStep {
  stage?: unknown;
  content?: unknown;
  step_number?: unknown;
  is_final?: unknown;
}

interface ProgressData {
  status?: string;
  complete?: boolean;
  chain_status?: string;
  partial_steps?: ProgressStep[];
}

function extractLatestStep(
  data: ProgressData
): ReasoningStepSummary | undefined {
  const partialSteps = data.partial_steps;
  if (!Array.isArray(partialSteps) || partialSteps.length === 0) {
    return undefined;
  }
  const last = partialSteps[partialSteps.length - 1];
  if (!last || typeof last.content !== 'string') {
    return undefined;
  }
  return {
    stage: typeof last.stage === 'string' ? last.stage : '',
    content: last.content,
    step_number: typeof last.step_number === 'number' ? last.step_number : 0,
    is_final: !!last.is_final,
  };
}

function classifyCompletion(
  data: ProgressData
): 'complete' | 'failed' | 'in_progress' {
  if (data.chain_status === 'failed') {
    return 'failed';
  }
  if (
    data.status === 'completed' ||
    data.complete === true ||
    data.chain_status === 'completed' ||
    data.chain_status === 'paused'
  ) {
    return 'complete';
  }
  return 'in_progress';
}

/**
 * WebSocket-first progress hook for the mounted action panel. Same
 * *parameter* signature as `useProposalPolling`, for drop-in use at the
 * panel's call site — driven internally by `useChatProgress` (WS with a
 * polling fallback) instead of pure polling. The return shape is
 * intentionally smaller: only `{ latestStep }`, since the caller doesn't
 * need `isPolling`/`progressDescription`.
 */
export function useProposalChatProgress(
  taskId: string | null,
  conversationId: string | null,
  orgId: string | undefined,
  options: UseProposalChatProgressOptions
) {
  const { onComplete, onError } = options;
  const [latestStep, setLatestStep] = useState<ReasoningStepSummary | null>(
    null
  );

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });
  useEffect(() => {
    onErrorRef.current = onError;
  });

  const previousTaskIdRef = useRef<string | null>(null);
  const wsFailedForTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Clear only when a NEW non-null task starts. When taskId goes null (a
    // tracked turn completed and clearPolling() reset it), keep the final
    // reasoning step visible instead of blanking it on the completion
    // transition — the panel still shows the last content until the next turn.
    if (taskId && taskId !== previousTaskIdRef.current) {
      setLatestStep(null);
      wsFailedForTaskIdRef.current = null;
    }
    previousTaskIdRef.current = taskId;
  }, [taskId]);

  const setLatestStepDeduped = useCallback((next: ReasoningStepSummary) => {
    setLatestStep(prev => {
      if (
        prev &&
        prev.step_number === next.step_number &&
        prev.content === next.content &&
        prev.stage === next.stage
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const onStepProgress = useCallback(
    (event: StepProgressEvent) => {
      setLatestStepDeduped({
        stage: event.step.stage,
        content: event.step.content,
        step_number: event.step.step_number,
        is_final: event.step.is_final,
      });
    },
    [setLatestStepDeduped]
  );

  // This hook does not dedupe completion signals — every WS `chain.completed`
  // and every poll-reported completion forwards to onComplete as-is. The
  // caller's tracker (chatTaskTracker's synchronous `resolved` guard) owns
  // exactly-once semantics; see ProposalDetailPanel.tsx:327-331.
  const onChainCompleted = useCallback(() => {
    logger.debug('[useProposalChatProgress] terminal via ws', {
      component: 'proposals',
      source: 'ws',
    });
    onCompleteRef.current();
  }, []);

  const onChainFailed = useCallback(
    (event: ChainFailedEvent) => {
      logger.debug('[useProposalChatProgress] failure via ws', {
        component: 'proposals',
        source: 'ws',
      });
      wsFailedForTaskIdRef.current = taskId;
      onErrorRef.current(
        new Error(
          event.error || 'Something went wrong processing your message.'
        )
      );
    },
    [taskId]
  );

  const onTitleUpdated = useCallback(() => {}, []);

  const onPollingUpdate = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polling data structure varies by task type
    (data: any) => {
      const progress = data as ProgressData;
      const next = extractLatestStep(progress);
      if (next) {
        setLatestStepDeduped(next);
      }

      const outcome = classifyCompletion(progress);
      if (outcome === 'failed') {
        logger.debug('[useProposalChatProgress] failure via poll', {
          component: 'proposals',
          source: 'poll',
        });
        onErrorRef.current(
          new Error('Something went wrong processing your message.')
        );
      } else if (outcome === 'complete') {
        // Forwarded as-is, no dedup against a prior WS completion — see the
        // comment on onChainCompleted above; the caller's tracker is what
        // guarantees exactly-once handling. The wsFailedForTaskIdRef check
        // below is a different guard: it covers a prior WS *failure* (not
        // completion) for this same task, which must never be silently
        // overridden by a later poll-based 'complete' read. It's scoped to
        // the current taskId so a late failure event for a superseded task
        // can't poison the next task's completion.
        if (wsFailedForTaskIdRef.current === taskId) {
          logger.warn(
            '[useProposalChatProgress] suppressed poll-complete after observed ws failure',
            {
              component: 'proposals',
              source: 'poll',
              taskId,
            }
          );
        } else {
          logger.debug('[useProposalChatProgress] terminal via poll', {
            component: 'proposals',
            source: 'poll',
          });
          onCompleteRef.current();
        }
      }
    },
    [setLatestStepDeduped, taskId]
  );

  const onPollingError = useCallback(
    (message: string) => {
      logger.warn('[useProposalChatProgress] polling error', {
        component: 'proposals',
        source: 'poll',
        taskId,
      });
      onErrorRef.current(new Error(message));
    },
    [taskId]
  );

  useChatProgress({
    orgId,
    conversationId,
    taskId,
    onStepProgress,
    onChainCompleted,
    onChainFailed,
    onTitleUpdated,
    onPollingUpdate,
    onPollingError,
    enabled: !!conversationId,
  });

  return { latestStep };
}
